/**
 * EmbedPicker.tsx — Modal-Picker fuer Scenes / Audits / Documents / Reports
 *
 * Laedt die Liste der jeweiligen Ressourcen aus dem Backend, zeigt sie als
 * scrollbare Liste mit Suchfeld und ruft `onSelect(item)` auf.
 *
 * Doc-Items zeigen ein Paperless-Thumbnail in der Auswahlliste.
 */

import { useEffect, useMemo, useState } from 'react'
import { getDocumentThumbnailUrl, fetchProjectDocuments } from '../../lib/paperless/api'

export type EmbedKind = 'scene' | 'audit' | 'doc' | 'report' | 'note'
import { extractExcerpt } from '../../lib/extractExcerpt'

// ── Datentypen ────────────────────────────────────────────────────────────

interface SceneDto { id: string; title: string; description: string; projectId?: string }
interface AuditDto { id: string; projectId: string; cloudId: string; data: Record<string, unknown> }
interface DocDto { id: string; title: string; cloudId: string }
interface ReportDto { id: string; title: string; content?: { type?: string; content?: unknown[] }; cloudId?: string }

export type PickedEmbed =
  | { kind: 'scene'; id: string; title: string; description: string }
  | { kind: 'audit'; id: string; title: string }
  | { kind: 'doc'; id: string; title: string }
  | { kind: 'report'; id: string; title: string; excerpt: string }
  | { kind: 'note'; id: string; title: string }

interface Props {
  kind: EmbedKind
  projectId: string
  projectCloudIds: string[]
  onSelect: (picked: PickedEmbed) => void
  onClose: () => void
}

export function EmbedPicker({ kind, projectId, projectCloudIds, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<PickedEmbed[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setItems(null)
    setError(null)

    async function load() {
      try {
        const { apiFetch: af, projectUrl: pu } = await import('../../lib/apiBase')
        if (kind === 'scene') {
          const r = await af(pu('/scenes'))
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          const json = await r.json()
          const data: SceneDto[] = json.data || json
          if (cancelled) return
          setItems(data.map((s) => ({ kind: 'scene', id: s.id, title: s.title, description: s.description || '' })))

        } else if (kind === 'audit') {
          // Audits leben primaer in script.js als _auditObjects (In-Memory + config.json).
          // Zuerst dort lesen, Fallback auf API falls leer.
          const liveAudits: Array<{ id: string; data: Record<string, unknown> }> =
            (window as any)._auditObjects || []

          if (liveAudits.length > 0) {
            if (cancelled) return
            setItems(liveAudits.map((a) => ({
              kind: 'audit' as const,
              id: a.id,
              title: (a.data?.bezeichnung as string) || (a.data?.title as string) || `Audit ${a.id.slice(0, 8)}`,
            })))
          } else {
            // Fallback: API (falls bulk-sync aktiv)
            const r = await af(pu('/audits'))
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            const data: AuditDto[] = await r.json()
            if (cancelled) return
            setItems(data.map((a) => ({
              kind: 'audit' as const,
              id: a.id,
              title: (a.data?.bezeichnung as string) || (a.data?.title as string) || `Audit ${a.id.slice(0, 8)}`,
            })))
          }

        } else if (kind === 'doc') {
          // Paperless-Dokumente laden, gefiltert nach Projekt-Tag (paperless_tag_id)
          const resp = await fetchProjectDocuments({ page_size: 100, ordering: '-created' })
          if (cancelled) return
          setItems(
            resp.results.map((d) => ({
              kind: 'doc' as const,
              id: String(d.id),
              title: d.title,
            })),
          )

        } else if (kind === 'report') {
          const r = await af(pu('/documents'))
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          const json = await r.json()
          const data: ReportDto[] = json.data || json
          if (cancelled) return
          const cloudSet = new Set(projectCloudIds)
          const filtered = cloudSet.size > 0 ? data.filter((d) => !d.cloudId || cloudSet.has(d.cloudId)) : data
          setItems(filtered.map((d) => ({
            kind: 'report',
            id: d.id,
            title: d.title,
            excerpt: extractExcerpt(d.content as any, 120),
          })))

        } else if (kind === 'note') {
          const r = await af(pu('/annotations'))
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          const json = await r.json()
          const data: Array<{ id: string; title: string; tooltip?: string; type?: string }> = json.data || json
          if (cancelled) return
          setItems(data.map((a) => ({
            kind: 'note' as const,
            id: a.id,
            title: a.title || `Annotation ${a.id.slice(0, 8)}`,
          })))
        }
      } catch (e: unknown) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : String(e))
      }
    }
    load()
    return () => { cancelled = true }
  }, [kind, projectId, projectCloudIds])

  const filtered = useMemo(() => {
    if (!items) return []
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((it) => it.title.toLowerCase().includes(q) || it.id.includes(q))
  }, [items, query])

  const heading = kind === 'scene' ? 'Szene einbetten'
    : kind === 'audit' ? 'Audit einbetten'
    : kind === 'doc' ? 'Dokument einbetten'
    : kind === 'report' ? 'Report einbetten'
    : kind === 'note' ? 'Annotation einbetten'
    : 'Einbetten'

  return (
    <div className="wb-picker__backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="wb-picker" data-component="EmbedPicker" onClick={(e) => e.stopPropagation()}>
        <div className="wb-picker__head">
          <h3>{heading}</h3>
          <button type="button" className="wb-picker__close" onClick={onClose} aria-label="Schliessen">×</button>
        </div>
        <input
          type="search"
          className="wb-picker__search"
          placeholder="Suchen…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="wb-picker__list">
          {error && <div className="wb-picker__error">Fehler: {error}</div>}
          {!error && items === null && <div className="wb-picker__loading">Lade …</div>}
          {!error && items !== null && filtered.length === 0 && (
            <div className="wb-picker__empty">Keine Eintraege gefunden.</div>
          )}
          {!error && filtered.map((it) => (
            <button key={it.id} type="button" className="wb-picker__item" onClick={() => onSelect(it)}>
              {/* Doc-Items mit Thumbnail */}
              {it.kind === 'doc' && <DocPickerThumb docId={it.id} />}
              <div className="wb-picker__item-content">
                <div className="wb-picker__item-title">{it.title || '(ohne Titel)'}</div>
                {it.kind === 'scene' && it.description && <div className="wb-picker__item-sub">{it.description}</div>}
                {it.kind === 'report' && it.excerpt && <div className="wb-picker__item-sub">{it.excerpt}</div>}
                <div className="wb-picker__item-id">{it.id}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Doc-Thumbnail in der Auswahlliste ─────────────────────────────────────

function DocPickerThumb({ docId }: { docId: string }) {
  const numericId = parseInt(docId, 10)
  const url = !isNaN(numericId) ? getDocumentThumbnailUrl(numericId) : null
  const [err, setErr] = useState(false)

  if (!url || err) {
    return <div className="wb-picker__thumb wb-picker__thumb--placeholder">📄</div>
  }

  return (
    <img
      className="wb-picker__thumb"
      src={url}
      alt=""
      onError={() => setErr(true)}
    />
  )
}
